/**
 * Layout（幾何配置）導出ユーティリティ。
 *
 * spec §3.1 の `AppState → Layout` マッピングを実装する pure 関数群。
 * DOM / Canvas には触らない。outer canvas サイズ / inner 多角形の
 * 内接矩形 / 各 pad 項を一元的に `Layout` オブジェクトへ畳み込む。
 *
 * 公開 API:
 * - `computeLayout(state)` — state → Layout（spec §3.1）
 * - `fitCanvas(containerW, containerH, outer)` — outer の表示 CSS サイズを算出
 *
 * 設計参照:
 * - docs/happa-designtool-v1.1-spec.md §3.1（レイアウト）
 * - docs/happa-designtool-v1.1-spec.md §A.2（DEFAULTS）
 * - wireframes/v0.10/wireframe.html L527–555（`computeLayout` / `fitCanvas`）
 */

import type { Layout } from '../core/types';
import type { AppState, AspectRatio } from '../state';

/**
 * Aspect ratio プリセット → inner の base (width, height)。
 *
 * spec §3.1 脚注「主被写体の領域（1080×1080 等）」に該当。outer サイズは
 * ここに 2 × pad を加えた値で、書き出し対象範囲の基礎寸法となる。
 * wireframe v0.10 L306–311 と完全一致。
 *
 * `'custom'` は可変寸法のためこの map には含めず、`computeLayout` 内で
 * `state.customAspect` から直接読む（Record のキーを
 * `Exclude<AspectRatio, 'custom'>` に絞ることで type-safe）。
 */
const INNER_BASE_DIMS: Readonly<Record<Exclude<AspectRatio, 'custom'>, readonly [number, number]>> =
  {
    '16:9': [1280, 720],
    '4:3': [1200, 900],
    '1:1': [1080, 1080],
    '9:16': [720, 1280],
  };

/** `fitCanvas` のステージ余白（px）。プロトタイプ CSS `padding: 48px` 左右合算相当。 */
const STAGE_FIT_PADDING = 96;

/**
 * `AppState` から 1 フレーム分の `Layout` を導出する（spec §3.1）。
 *
 * 導出順:
 * 1. `aspectRatio` → inner base `(innerW, innerH)`
 *    - preset（`'16:9'` / `'4:3'` / `'1:1'` / `'9:16'`）は `INNER_BASE_DIMS` を参照
 *    - `'custom'` のときは `state.customAspect.{width,height}` をそのまま使用
 *      （入力範囲バリデーションは UI 層の責務、layout は値を信用する）
 * 2. `innerShort = min(innerW, innerH)`
 * 3. `gutter = innerShort × gutterPct%`
 * 4. `bleed  = gutter × bleedMult`
 * 5. `fontSize = innerShort × fontSizePct%`
 * 6. `textMargin = innerShort × textMarginPct%`
 * 7. `textBand   = fontSize × 1.6`
 * 8. `pad = gutter + bleed + textMargin + textBand + fontSize × 0.4`
 * 9. `outerW/H = innerW/H + 2 × pad`、`innerX0/Y0 = pad`
 *
 * 全項が純粋な算術のみ。副作用ゼロ。呼び出しごとに新しい `Layout` を返す。
 *
 * @param state 現在の `AppState`。参照するのは aspect/layout/fontSize 関連のみ。
 * @returns `Layout` オブジェクト。
 */
export function computeLayout(state: AppState): Layout {
  const [innerW, innerH] =
    state.aspectRatio === 'custom'
      ? ([state.customAspect.width, state.customAspect.height] as const)
      : INNER_BASE_DIMS[state.aspectRatio];
  const innerShort = Math.min(innerW, innerH);

  const gutter = innerShort * (state.gutterPct / 100);
  const bleed = gutter * state.bleedMult;
  const fontSize = innerShort * (state.fontSizePct / 100);
  const textMargin = innerShort * (state.textMarginPct / 100);
  const textBand = fontSize * 1.6;
  // `fontSize × 0.4` は文字の下端に余白を確保する項（spec §3.1 数式）。
  const pad = gutter + bleed + textMargin + textBand + fontSize * 0.4;

  const outerW = innerW + 2 * pad;
  const outerH = innerH + 2 * pad;

  return {
    innerW,
    innerH,
    innerShort,
    gutter,
    bleed,
    textMargin,
    fontSize,
    textBand,
    pad,
    outerW,
    outerH,
    innerX0: pad,
    innerY0: pad,
    innerX1: pad + innerW,
    innerY1: pad + innerH,
  };
}

/** `fitCanvas` の戻り値（Canvas の CSS 表示サイズ、未丸め px）。 */
export interface FitCanvasResult {
  /** `canvas.style.width` に設定すべき px 値。 */
  cssWidth: number;
  /** `canvas.style.height` に設定すべき px 値。 */
  cssHeight: number;
}

/**
 * `drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh)` の **source** 矩形を
 * 「cover fit」ルールで導出する pure 関数（Phase F Step 4 / overlay inner /
 * background レイヤー）。
 *
 * "cover" はターゲット矩形を**完全に覆う**最小の source 矩形を選び、余剰は
 * 切り捨てる（CSS `object-fit: cover` と同じセマンティクス）。destination は
 * ターゲット全面を使うため、呼び出し側は `drawImage(..., 0, 0, targetW,
 * targetH)` で貼る。
 *
 * アルゴリズム:
 * - 画像アスペクト比 (`imgW / imgH`) が target より大きい（= 横長）なら、
 *   source の高さ全部（sh = imgH）を使い、幅を target 比に合わせて `imgH ×
 *   targetAspect` にクロップ（中央寄せ）。
 * - それ以外（= 縦長 or 同比）なら、source の幅全部（sw = imgW）を使い、
 *   高さを `imgW / targetAspect` にクロップ（中央寄せ）。
 *
 * ゼロ除算（`targetH === 0` 等）は呼び出し側で弾く前提（本関数は防御しない）。
 *
 * @param imgW 画像の自然幅（`naturalWidth`）。
 * @param imgH 画像の自然高（`naturalHeight`）。
 * @param targetW 貼り付けたい矩形の幅。
 * @param targetH 貼り付けたい矩形の高さ。
 * @returns `drawImage` の source 側 9-arg 形式で使う `{sx, sy, sw, sh}`。
 */
export function computeCoverRect(
  imgW: number,
  imgH: number,
  targetW: number,
  targetH: number,
): { sx: number; sy: number; sw: number; sh: number } {
  const imgAspect = imgW / imgH;
  const targetAspect = targetW / targetH;
  if (imgAspect > targetAspect) {
    // 画像が相対的に横長 → 高さ全部を使い、幅を target 比でクロップ
    const sh = imgH;
    const sw = imgH * targetAspect;
    return { sx: (imgW - sw) / 2, sy: 0, sw, sh };
  }
  // 画像が相対的に縦長（or 同比） → 幅全部を使い、高さを target 比でクロップ
  const sw = imgW;
  const sh = imgW / targetAspect;
  return { sx: 0, sy: (imgH - sh) / 2, sw, sh };
}

/**
 * outer 寸法をステージ可視領域に contain-fit させた CSS 表示サイズを返す。
 *
 * プロトタイプ / wireframe L549–552 と同等:
 * ```
 * availW = containerWidth  - STAGE_FIT_PADDING
 * availH = containerHeight - STAGE_FIT_PADDING
 * scale  = min(availW / outerW, availH / outerH, 1)  // 1 で上限クランプ（拡大禁止）
 * css    = [outerW × scale, outerH × scale]
 * ```
 *
 * 本関数は pure。丸めは呼び出し側の責任（`canvas.style.width = "${n}px"` で OK）。
 *
 * v1.0 の `fitCanvas`（`src/ui/stage.ts` 内の複製版）と単一化するため、
 * render 層の計算ロジックを本モジュールに集約する（spec §10.2 Phase A の
 * 「stage.ts が別途複製していたロジックを統一」に相当）。
 *
 * @param containerWidth  `.stage` のクライアント幅（px）。
 * @param containerHeight `.stage` のクライアント高（px）。
 * @param outer outer canvas の物理寸法 `[outerW, outerH]`（`computeLayout` 由来）。
 * @returns CSS 表示サイズ（未丸め）。
 */
export function fitCanvas(
  containerWidth: number,
  containerHeight: number,
  outer: readonly [number, number],
): FitCanvasResult {
  const [outerW, outerH] = outer;
  const availW = Math.max(0, containerWidth - STAGE_FIT_PADDING);
  const availH = Math.max(0, containerHeight - STAGE_FIT_PADDING);
  // outerW / outerH が 0 の場合はゼロ除算を避ける（退化入力の保険）。
  const sx = outerW > 0 ? availW / outerW : 0;
  const sy = outerH > 0 ? availH / outerH : 0;
  const scale = Math.min(sx, sy, 1);
  return {
    cssWidth: outerW * scale,
    cssHeight: outerH * scale,
  };
}
